import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useOperationalStore from '../store/useOperationalStore';
import { ICONS, EDGE_ITEMS } from './navConfig';
import './EdgePanel.css';

// Edge Panel — fullscreen-only quick nav. A slim translucent handle on the
// right edge slides out to the user's pinned views (max 6, persisted per
// browser), plus exit-fullscreen. Samsung Edge Panel energy, REKON palette.

const MAX_PINS = 6;
const STORAGE_KEY = 'rekon_edge_views';
const DEFAULT_PINS = ['flow', 'priority', 'command', 'shop-map', 'road-service', 'schedule'];

const ALL_ITEMS = EDGE_ITEMS;

function loadPins() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (Array.isArray(saved) && saved.length) {
      return saved.filter(id => ALL_ITEMS.some(item => item.id === id)).slice(0, MAX_PINS);
    }
  } catch {}
  return DEFAULT_PINS;
}

export default function EdgePanel() {
  const isFs = useOperationalStore(s => s.isViewFullscreen);
  const setFs = useOperationalStore(s => s.setViewFullscreen);
  const activeView = useOperationalStore(s => s.activeView);
  const setActiveView = useOperationalStore(s => s.setActiveView);
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pins, setPins] = useState(loadPins);
  const rootRef = useRef(null);

  const pinnedItems = useMemo(
    () => pins.map(id => ALL_ITEMS.find(item => item.id === id)).filter(Boolean),
    [pins]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    function onMouse(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setEditing(false);
      }
    }
    document.addEventListener('mousedown', onMouse);
    return () => document.removeEventListener('mousedown', onMouse);
  }, [open]);

  if (!isFs) return null;

  function go(item) {
    if (item.route) {
      navigate(item.route);
    } else {
      setActiveView(item.id);
      if (location.pathname !== '/flow') navigate('/flow');
    }
    setOpen(false);
    setEditing(false);
  }

  function isActive(item) {
    return item.route
      ? location.pathname.startsWith(item.route)
      : location.pathname === '/flow' && activeView === item.id;
  }

  function togglePin(id) {
    setPins(prev => {
      const next = prev.includes(id)
        ? prev.filter(p => p !== id)
        : prev.length < MAX_PINS ? [...prev, id] : prev;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }

  function exitFullscreen() {
    document.exitFullscreen?.().catch(() => {});
    setFs(false);
  }

  return (
    <div className={`edge${open ? ' edge--open' : ''}`} ref={rootRef}>
      <button
        className="edge-handle"
        title="Quick views"
        onClick={() => { setOpen(v => !v); setEditing(false); }}
        aria-expanded={open}
      />
      {open && (
        <div className="edge-panel">
          {!editing ? (
            <>
              {pinnedItems.map(item => (
                <button
                  key={item.id}
                  className={`edge-item${isActive(item) ? ' edge-item--active' : ''}`}
                  onClick={() => go(item)}
                  title={item.label}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {ICONS[item.icon]}
                  </svg>
                  <span>{item.label}</span>
                </button>
              ))}
              <div className="edge-sep" />
              <button className="edge-item edge-item--dim" onClick={() => setEditing(true)} title="Customize pinned views">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.2 4.2l2.8 2.8M17 17l2.8 2.8M1 12h4M19 12h4M4.2 19.8L7 17M17 7l2.8-2.8"/>
                </svg>
                <span>Customize</span>
              </button>
              <button className="edge-item edge-item--dim" onClick={exitFullscreen} title="Exit fullscreen">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
                  <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
                </svg>
                <span>Exit Fullscreen</span>
              </button>
            </>
          ) : (
            <div className="edge-edit">
              <div className="edge-edit-head">
                <span>Pinned views <em>{pins.length}/{MAX_PINS}</em></span>
                <button onClick={() => setEditing(false)}>Done</button>
              </div>
              <div className="edge-edit-list">
                {ALL_ITEMS.map(item => {
                  const pinned = pins.includes(item.id);
                  const full = !pinned && pins.length >= MAX_PINS;
                  return (
                    <button
                      key={item.id}
                      className={`edge-edit-row${pinned ? ' is-pinned' : ''}`}
                      disabled={full}
                      onClick={() => togglePin(item.id)}
                    >
                      <span>{item.label}</span>
                      <span className="edge-edit-mark">{pinned ? '●' : '○'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
